// react
import * as React from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import CookieOutlinedIcon from '@mui/icons-material/CookieOutlined';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import WbSunnyRoundedIcon from '@mui/icons-material/WbSunnyRounded';
import WidthFullRoundedIcon from '@mui/icons-material/WidthFullRounded';
import WidthNormalRoundedIcon from '@mui/icons-material/WidthNormalRounded';

// material ui
import {
  Box,
  Button,
  ButtonBase,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// shared components
import { SuperuserOnly } from '@/components/access-control';
import InsetDrawer from '@/components/ui/drawer/InsetDrawer';

// components
import { useAdminViewMode } from '@/contexts/AdminViewModeContext';
import { useColorMode } from '@/contexts/ColorModeContext';
import { useContainerLayout } from '@/contexts/ContainerLayoutContext';
import { useCookiePreferences } from '@/contexts/CookiePreferencesContext';

// relative
import AvatarComponent from '../user-menu/AvatarComponent';
import { useUserMenuState } from '../user-menu/useUserMenuState';

export default function SettingsMenu() {
  const theme = useTheme();
  const { t: tApp } = useTranslation('app');
  const { mode, toggleColorMode } = useColorMode();
  const { fullWidth, toggleFullWidth } = useContainerLayout();
  const { adminViewMode, setAdminViewMode } = useAdminViewMode();
  const { openCookiePreferences } = useCookiePreferences();
  const {
    t,
    email,
    greetingText,
    currentLanguage,
    changeLanguage,
    handleLogout,
  } = useUserMenuState();

  const [open, setOpen] = React.useState(false);

  const handleThemeChange = (
    _event: React.MouseEvent<HTMLElement>,
    value: string | null,
  ) => {
    if (value && value !== mode) toggleColorMode();
  };

  const handleLayoutChange = (
    _event: React.MouseEvent<HTMLElement>,
    value: string | null,
  ) => {
    if (value && (value === 'full') !== fullWidth) toggleFullWidth();
  };

  const handleAdminViewModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    value: string | null,
  ) => {
    if (value && (value === 'admin') !== adminViewMode) {
      setAdminViewMode(value === 'admin');
    }
  };

  const handleLanguageChange = (
    _event: React.MouseEvent<HTMLElement>,
    value: string | null,
  ) => {
    if (value) changeLanguage(value);
  };

  const handleCookiePreferencesClick = () => {
    setOpen(false);
    openCookiePreferences();
  };

  return (
    <>
      <ButtonBase
        onClick={() => setOpen(true)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderRadius: 999,
          pl: 1,
          pr: 0.5,
          py: 0.5,
          border: `1px solid ${theme.palette.divider}`,
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
        data-testid="appbar-settings-button"
        aria-label={t('toolbar.settings')}
      >
        <SettingsRoundedIcon sx={{ color: 'text.secondary' }} />
        <AvatarComponent string={email} size={32} />
      </ButtonBase>

      <InsetDrawer
        open={open}
        onClose={() => setOpen(false)}
        title={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Typography variant="subtitle1" color="text.secondary">
              {greetingText}
            </Typography>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {email}
            </Typography>
            <SuperuserOnly>
              <AdminPanelSettingsRoundedIcon
                fontSize="small"
                sx={{ color: 'text.secondary' }}
              />
            </SuperuserOnly>
          </Box>
        }
        width={420}
        footer={
          <Button
            fullWidth
            onClick={handleLogout}
            startIcon={<LogoutRoundedIcon fontSize="small" />}
            data-testid="appbar-settings-logout"
            sx={{
              justifyContent: 'center',
              color: 'text.secondary',
              ':hover': { color: 'error.main' },
            }}
          >
            {t('toolbar.logout')}
          </Button>
        }
      >
        <Stack spacing={3}>
          <SuperuserOnly>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                gutterBottom
                sx={{ display: 'block' }}
              >
                {t('toolbar.adminViewTitle', { defaultValue: 'View Mode' })}
              </Typography>
              <ToggleButtonGroup
                value={adminViewMode ? 'admin' : 'account'}
                exclusive
                fullWidth
                onChange={handleAdminViewModeChange}
                data-testid="appbar-settings-admin-view-group"
              >
                <ToggleButton
                  value="account"
                  data-testid="appbar-settings-admin-view-account"
                >
                  <PersonRoundedIcon fontSize="small" sx={{ mr: 1 }} />
                  {t('common.accountView', { defaultValue: 'Account View' })}
                </ToggleButton>
                <ToggleButton
                  value="admin"
                  data-testid="appbar-settings-admin-view-admin"
                >
                  <AdminPanelSettingsRoundedIcon
                    fontSize="small"
                    sx={{ mr: 1 }}
                  />
                  {t('common.adminView', { defaultValue: 'Admin View' })}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </SuperuserOnly>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              gutterBottom
              sx={{ display: 'block' }}
            >
              {tApp('theme.title')}
            </Typography>
            <ToggleButtonGroup
              value={mode}
              exclusive
              fullWidth
              onChange={handleThemeChange}
              data-testid="appbar-settings-theme-group"
            >
              <ToggleButton
                value="light"
                data-testid="appbar-settings-theme-light"
              >
                <WbSunnyRoundedIcon fontSize="small" sx={{ mr: 1 }} />
                {tApp('theme.light')}
              </ToggleButton>
              <ToggleButton
                value="dark"
                data-testid="appbar-settings-theme-dark"
              >
                <DarkModeRoundedIcon fontSize="small" sx={{ mr: 1 }} />
                {tApp('theme.dark')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              gutterBottom
              sx={{ display: 'block' }}
            >
              {tApp('layout.title')}
            </Typography>
            <ToggleButtonGroup
              value={fullWidth ? 'full' : 'standard'}
              exclusive
              fullWidth
              onChange={handleLayoutChange}
              data-testid="appbar-settings-layout-group"
            >
              <ToggleButton
                value="standard"
                data-testid="appbar-settings-layout-standard"
              >
                <WidthNormalRoundedIcon fontSize="small" sx={{ mr: 1 }} />
                {tApp('layout.standardWidth')}
              </ToggleButton>
              <ToggleButton
                value="full"
                data-testid="appbar-settings-layout-full"
              >
                <WidthFullRoundedIcon fontSize="small" sx={{ mr: 1 }} />
                {tApp('layout.fullWidth')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              gutterBottom
              sx={{ display: 'block' }}
            >
              {t('toolbar.languageTitle')}
            </Typography>
            <ToggleButtonGroup
              value={currentLanguage}
              exclusive
              fullWidth
              onChange={handleLanguageChange}
              data-testid="appbar-settings-language-group"
            >
              <ToggleButton
                value="en"
                data-testid="appbar-settings-language-en"
              >
                {t('toolbar.languageEnglish')}
              </ToggleButton>
              <ToggleButton
                value="dk"
                data-testid="appbar-settings-language-dk"
              >
                {t('toolbar.languageDanish')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              gutterBottom
              sx={{ display: 'block' }}
            >
              {tApp('cookiePreferences.title')}
            </Typography>
            <Button
              fullWidth
              variant="contained"
              startIcon={<CookieOutlinedIcon fontSize="small" />}
              onClick={handleCookiePreferencesClick}
              data-testid="appbar-open-cookie-preferences"
            >
              {t('toolbar.cookiePreferences')}
            </Button>
          </Box>
        </Stack>
      </InsetDrawer>
    </>
  );
}
